// ARQ review queue: atlas-admins approve/reject apps awaiting first publish (Slice 6).
import { useCallback, useEffect, useState } from "react";
import Alert from "@mui/material/Alert";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import ShieldPersonIcon from "@mui/icons-material/AdminPanelSettings";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import { alpha } from "@mui/material/styles";

import type { Client, PendingApproval } from "../types";
import { MONO } from "../theme";
import { EmptyState, LoadingRows, PageHeader } from "../components/ui";

export function ArqReview({ client }: { client: Client }) {
  const [pending, setPending] = useState<PendingApproval[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null); // "app@ver" currently being decided

  const refresh = useCallback(() => {
    setError(null);
    client
      .listPendingApprovals()
      .then(setPending)
      .catch((e) => setError((e as Error).message));
  }, [client]);

  useEffect(refresh, [refresh]);

  async function decide(req: PendingApproval, approve: boolean) {
    const key = `${req.appName}@${req.appVersion}`;
    setBusy(key);
    try {
      if (approve) await client.approveApp(req.appName, req.appVersion);
      else await client.rejectApp(req.appName, req.appVersion, "Rejected from Console");
      refresh();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  }

  // Secondary-tinted "atlas-admins" pill (this queue is admin-only).
  const adminChip = (
    <Chip
      icon={<ShieldPersonIcon sx={{ fontSize: 16 }} />}
      label="atlas-admins"
      sx={(theme) => ({
        bgcolor: alpha(theme.palette.secondary.main, theme.palette.mode === "dark" ? 0.16 : 0.08),
        color: "secondary.dark",
        "& .MuiChip-icon": { color: "secondary.main" },
      })}
    />
  );

  const header = (
    <PageHeader
      title="Review queue"
      subtitle="New apps awaiting first-publish approval. Approve to make them live, or reject."
      action={adminChip}
    />
  );

  let body;
  if (error) {
    body = (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={refresh}>
            Retry
          </Button>
        }
      >
        {error}
      </Alert>
    );
  } else if (pending === null) {
    body = <LoadingRows rows={3} />;
  } else if (pending.length === 0) {
    body = (
      <EmptyState
        icon={<TaskAltIcon />}
        title="Nothing awaiting approval"
        hint="You're all caught up. New apps submitted for their first publish will show up here for review."
        accent="success"
      />
    );
  } else {
    body = (
      <TableContainer component={Paper} variant="outlined">
        <Table sx={{ minWidth: 760 }}>
          <TableHead>
            <TableRow>
              <TableCell>App</TableCell>
              <TableCell sx={{ width: 140 }}>Version</TableCell>
              <TableCell sx={{ width: 220 }}>Submitted</TableCell>
              <TableCell sx={{ width: 240 }} align="right">
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pending.map((req) => {
              const key = `${req.appName}@${req.appVersion}`;
              return (
                <TableRow key={key} sx={{ "&:last-child td": { border: 0 } }}>
                  <TableCell>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Avatar
                        variant="rounded"
                        sx={{
                          width: 36,
                          height: 36,
                          bgcolor: "secondary.dark",
                          color: "secondary.contrastText",
                          fontSize: 16,
                          fontWeight: 600,
                        }}
                      >
                        {req.appName.slice(0, 1).toUpperCase()}
                      </Avatar>
                      <Typography variant="subtitle2">{req.appName}</Typography>
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ fontFamily: MONO, color: "text.secondary" }}>{req.appVersion}</TableCell>
                  <TableCell sx={{ color: "text.secondary" }}>{formatTime(req.time)}</TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={1} justifyContent="flex-end" flexWrap="wrap" useFlexGap>
                      <Button
                        size="small"
                        color="error"
                        startIcon={<CloseIcon />}
                        disabled={busy === key}
                        onClick={() => decide(req, false)}
                      >
                        Reject
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<CheckIcon />}
                        disabled={busy === key}
                        onClick={() => decide(req, true)}
                      >
                        Approve
                      </Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  return (
    <Stack spacing={3}>
      {header}
      {body}
    </Stack>
  );
}

function formatTime(ms: number) {
  if (!ms) return "";
  return new Date(ms).toISOString().slice(0, 16).replace("T", " ");
}
